"use client";

import React, { useCallback, useEffect } from "react";

import CharacterCount from "@tiptap/extension-character-count";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
    AlignCenter,
    AlignJustify,
    AlignLeft,
    AlignRight,
    Bold,
    Clipboard,
    Copy,
    Heading1,
    Heading2,
    Heading3,
    Highlighter,
    Italic,
    List,
    ListOrdered,
    Palette,
    Pilcrow,
    Redo,
    Strikethrough,
    Type,
    Underline as UnderlineIcon,
    Undo,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "utils/cn";

import Button from "components/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "components/DropdownMenu";
import { Popover, PopoverContent, PopoverTrigger } from "components/Popover";
import Separator from "components/Separator";
import Spinner from "components/Spinner";

import type { ReportTemplateResponse } from "../../types/ReportTemplates.type";

interface TiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  isSummarize?: boolean;
  reportTemplates?: ReportTemplateResponse[];
  applyTemplate?: (templateId: string) => void;
  children?: React.ReactNode;
}

const COLORS = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#b7b7b7",
  "#cccccc",
  "#d9d9d9",
  "#efefef",
  "#f3f3f3",
  "#ffffff",
  "#980000",
  "#ff0000",
  "#ff9900",
  "#ffff00",
  "#00ff00",
  "#00ffff",
  "#4a86e8",
  "#0000ff",
  "#9900ff",
  "#ff00ff",
  "#e6b8af",
  "#f4cccc",
  "#fce5cd",
  "#fff2cc",
  "#d9ead3",
  "#d0e0e3",
  "#c9daf8",
  "#cfe2f3",
  "#d9d2e9",
  "#ead1dc",
];

const HIGHLIGHT_COLORS = ["#fef08a", "#bbf7d0", "#bfdbfe", "#fecaca", "#ddd6fe", "#fed7aa", "#99f6e4", "#fda4af"];

export default function TiptapEditor({
  content,
  onChange,
  placeholder = "",
  isSummarize,
  reportTemplates = [],
  applyTemplate,
  children,
}: TiptapEditorProps) {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        underline: false,
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: "list-disc pl-6 my-2",
          },
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
          HTMLAttributes: {
            class: "list-decimal pl-6 my-2",
          },
        },
        listItem: {
          HTMLAttributes: {
            class: "my-1",
          },
        },
        blockquote: {
          HTMLAttributes: {
            class: "border-l-4 border-general-gray-400 pl-4 my-4 italic text-general-gray-400",
          },
        },
        horizontalRule: {
          HTMLAttributes: {
            class: "my-6 border-t border-general-gray-300",
          },
        },
        heading: {
          levels: [1, 2, 3],
          HTMLAttributes: {
            class: "font-bold",
          },
        },
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-general-green-80] underline cursor-pointer hover:text-general-green-70]",
        },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass:
          "before:content-[attr(data-placeholder)] before:text-general-gray-400 before:float-left before:h-0 before:pointer-events-none",
      }),
      TaskList.configure({
        HTMLAttributes: {
          class: "not-prose pl-0 my-2",
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: "flex items-start gap-2 my-1",
        },
      }),
      Subscript,
      Superscript,
      TextStyle,
      Color,
      CharacterCount.configure({
        limit: 10000,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose-base max-w-none focus:outline-none min-h-[300px] p-4",
      },
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  const handleCopy = useCallback(() => {
    if (editor) {
      // Get the full HTML content of the editor
      const html = editor.getHTML();
      // Use Clipboard API to copy as HTML (with fallback)
      if (navigator.clipboard && window.ClipboardItem) {
        const blob = new Blob([html], { type: "text/html" });
        const item = new window.ClipboardItem({ "text/html": blob });
        navigator.clipboard.write([item]);
      } else {
        // Fallback: copy as plain text
        navigator.clipboard.writeText(html);
      }
    }
  }, [editor]);

  const handlePaste = useCallback(async () => {
    if (editor) {
      const text = await navigator.clipboard.readText();
      editor.chain().focus().insertContent(text).run();
    }
  }, [editor]);

  if (!editor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  const ToolbarButton = ({
    onClick,
    isActive = false,
    disabled = false,
    children,
    title,
  }: {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
  }) => (
    <Button
      type="button"
      variant="ghost"
      size="small"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "h-8 px-2 hover:bg-general-gray-200 w-fit active:bg-general-gray-300",
        isActive && "bg-general-gray-200 text-general-gray-900",
      )}
    >
      {children}
    </Button>
  );

  return (
    <div className="border rounded-lg overflow-hidden bg-white border-general-gray-400">
      {/* Toolbar */}
      <div className="border-b p-2 flex flex-wrap gap-0.5 items-center border-general-gray-400 bg-general-gray-200">
        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>

        {/* Copy/Paste */}
        <ToolbarButton onClick={handleCopy} title="Copy (Ctrl+C)">
          <Copy className="h-4 w-4" /> {t("editor.copy")}
        </ToolbarButton>
        <ToolbarButton onClick={handlePaste} title="Paste (Ctrl+V)">
          <Clipboard className="h-4 w-4" />
        </ToolbarButton>

        {/* Headings Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="small" className="h-8 gap-1 px-2 hover:bg-general-gray-200">
              <Type className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onClick={() => editor.chain().focus().setParagraph().run()}
              className={editor.isActive("paragraph") ? "bg-general-gray-200" : ""}
            >
              <Pilcrow className="h-4 w-4 mr-2" />
              Paragraph
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={editor.isActive("heading", { level: 1 }) ? "bg-general-gray-200" : ""}
            >
              <Heading1 className="h-4 w-4 mr-2" />
              Heading 1
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={editor.isActive("heading", { level: 2 }) ? "bg-general-gray-200" : ""}
            >
              <Heading2 className="h-4 w-4 mr-2" />
              Heading 2
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={editor.isActive("heading", { level: 3 }) ? "bg-general-gray-200" : ""}
            >
              <Heading3 className="h-4 w-4 mr-2" />
              Heading 3
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Text Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive("underline")}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive("strike")}
          title="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </ToolbarButton>

        {/* Text Color */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="small"
              className="h-8 w-8 p-0 hover:bg-general-gray-200 active:bg-general-gray-300"
              title="Text Color"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <p className="text-xs text-general-gray-400 mb-2">{t("editor.textColor")}</p>
            <div className="grid grid-cols-10 gap-1">
              {COLORS.map((color) => {
                const isActiveColor = editor.isActive("textStyle", { color });
                return (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      "w-5 h-5 rounded border border-general-gray-300 hover:scale-110 transition-transform",
                      isActiveColor && "ring-2 [--tw-ring-color:var(--color-general-green-800)] ring-offset-2",
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => editor.chain().focus().setColor(color).run()}
                  />
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="small"
              className="w-full mt-2 text-xs"
              onClick={() => editor.chain().focus().unsetColor().run()}
            >
              {t("editor.resetColor")}
            </Button>
          </PopoverContent>
        </Popover>

        {/* Highlight */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "h-8 w-8 p-0 hover:bg-general-gray-200 active:bg-general-gray-300",
                editor.isActive("highlight") && "bg-general-gray-300",
              )}
              title="Highlight"
            >
              <Highlighter className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <p className="text-xs text-general-gray-400 mb-2">{t("editor.highlightColor")}</p>
            <div className="grid grid-cols-4 gap-1">
              {HIGHLIGHT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className="w-7 h-7 rounded border border-general-gray-300 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color }}
                  onClick={() => editor.chain().focus().toggleHighlight({ color }).run()}
                />
              ))}
            </div>
            <Button
              variant="ghost"
              size="small"
              className="w-full mt-2 text-xs"
              onClick={() => editor.chain().focus().unsetHighlight().run()}
            >
              {t("editor.removeHighlight")}
            </Button>
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="mx-1 h-6" />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>

        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          isActive={editor.isActive({ textAlign: "left" })}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          isActive={editor.isActive({ textAlign: "center" })}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          isActive={editor.isActive({ textAlign: "right" })}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          isActive={editor.isActive({ textAlign: "justify" })}
          title="Justify"
        >
          <AlignJustify className="h-4 w-4" />
        </ToolbarButton>
      </div>
      {isSummarize && (
        <div className="border-b p-2 flex flex-wrap gap-0.5 items-center border-general-gray-400 bg-general-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={reportTemplates.length === 0}>
              <Button variant="ghost" size="small" className="h-8 gap-1 px-2 hover:bg-general-gray-200">
                {t("session.applyTemplate")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {reportTemplates.map((template: ReportTemplateResponse) => (
                <DropdownMenuItem key={template.id} onClick={() => applyTemplate?.(template.id)}>
                  {template.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {children}
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* Character Count */}
      <div className="border-t px-4 py-2 text-xs text-general-gray-400 flex justify-end gap-4 [background-color:rgba(239,243,247,0.2)]">
        <span>
          {editor.storage.characterCount.characters()} {t("editor.characters")}
        </span>
        <span>
          {editor.storage.characterCount.words()} {t("editor.words")}
        </span>
      </div>
    </div>
  );
}
